import { Classes, Spinner } from "@blueprintjs/core";
import { observer } from "mobx-react";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { useCallback, useEffect, useRef, useState } from "react";
import { DeviceStatus, SessionType } from "../models/session";
import styles from "./DeviceStep.module.scss";

const CODE_LENGTH = 6;

/** Как часто спрашиваем, не подтвердил ли устройство администратор */
const POLL_WAITING_ADMIN = 5000;
const POLL_WITH_CODE = 15000;

/**
 * Второй шаг входа с нового устройства: пароль уже принят, осталось ввести код из
 * Telegram или письма. Если отправить код некуда, ждём администратора - вход
 * продолжится сам, как только он подтвердит устройство.
 */
export const DeviceStep = observer(({ store }: { store: SessionType }) => {
    const [status, setStatus] = useState<DeviceStatus | null>(null);
    const [code, setCode] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    // момент, когда можно снова запросить код: отсчёт идёт локально, без запросов
    const [retryAt, setRetryAt] = useState(0);
    const [now, setNow] = useState(() => Date.now());
    const input = useRef<HTMLInputElement | null>(null);
    const finishing = useRef(false);

    const accept = useCallback(
        async (next: DeviceStatus | null) => {
            if (!next) {
                return;
            }

            setStatus(next);
            setRetryAt(Date.now() + next.retryAfter * 1000);

            if (next.state === "confirmed" && !finishing.current) {
                finishing.current = true;
                const ok = await store.finishDeviceConfirmation();
                finishing.current = ok;
            }

            if (next.state === "missing") {
                // устройства для этого входа нет (почистили cookie) - заново с формы входа
                store.cancelDeviceConfirmation();
            }
        },
        [store]
    );

    // при открытии: состояние, и если действующего кода нет - сразу отправим новый
    useEffect(() => {
        let alive = true;

        (async () => {
            const current = await store.deviceStatus();
            if (!alive || !current) {
                return;
            }

            if (current.state === "pending" && !current.codeActive && !current.needAdmin && current.retryAfter === 0) {
                const sent = await store.sendDeviceCode();
                alive && accept(sent);
            } else {
                accept(current);
            }
        })();

        return () => {
            alive = false;
        };
    }, [store, accept]);

    // устройство может подтвердить администратор, пока человек ждёт код
    const pending = status?.state === "pending";
    const needAdmin = !!status?.needAdmin;
    useEffect(() => {
        if (!pending) {
            return;
        }

        const timer = window.setInterval(
            async () => {
                const current = await store.deviceStatus();
                current?.state !== "pending" && accept(current);
            },
            needAdmin ? POLL_WAITING_ADMIN : POLL_WITH_CODE
        );

        return () => window.clearInterval(timer);
    }, [store, accept, pending, needAdmin]);

    const waitLeft = Math.max(0, Math.ceil((retryAt - now) / 1000));
    useEffect(() => {
        if (waitLeft <= 0) {
            return;
        }

        const timer = window.setTimeout(() => setNow(Date.now()), 1000);
        return () => window.clearTimeout(timer);
    }, [waitLeft, now]);

    const verify = async (value: string) => {
        if (value.length !== CODE_LENGTH || busy) {
            return;
        }

        setBusy(true);
        const result = await store.verifyDeviceCode(value);
        setBusy(false);

        if (result?.message) {
            setError(result.message);
            setCode("");
            input.current?.focus();
        }

        accept(result);
    };

    const resend = async () => {
        setBusy(true);
        const result = await store.sendDeviceCode();
        setBusy(false);

        setError(result?.message ?? null);
        setCode("");
        input.current?.focus();
        accept(result);
    };

    const back = (
        <button type="button" className={styles.link} onClick={() => store.cancelDeviceConfirmation()}>
            Войти под другим логином
        </button>
    );

    if (!status) {
        return (
            <div className={`${Classes.DIALOG_BODY} ${styles.loading}`}>
                <Spinner size={24} />
            </div>
        );
    }

    if (status.state === "locked") {
        return (
            <>
                <div className={Classes.DIALOG_BODY}>
                    <div className={styles.title}>Вход заблокирован</div>
                    <div className={styles.text}>
                        Администратор закрыл доступ с этого устройства. Если это ошибка — обратитесь к нему.
                    </div>
                </div>
                <div className={`${Classes.DIALOG_FOOTER} ${styles.footer}`}>{back}</div>
            </>
        );
    }

    if (status.needAdmin) {
        return (
            <>
                <div className={Classes.DIALOG_BODY}>
                    <div className={styles.title}>Устройство ждёт подтверждения</div>
                    <div className={styles.text}>
                        Кода отправить некуда: в профиле нет ни почты, ни Telegram. Попросите администратора подтвердить
                        вход — как только он это сделает, вы войдёте автоматически.
                    </div>
                    {status.device && <div className={styles.device}>{status.device}</div>}
                    <div className={styles.waiting}>
                        <Spinner size={16} />
                        Ждём подтверждения…
                    </div>
                </div>
                <div className={`${Classes.DIALOG_FOOTER} ${styles.footer}`}>{back}</div>
            </>
        );
    }

    return (
        <>
            <div className={Classes.DIALOG_BODY}>
                <div className={styles.title}>Подтвердите устройство</div>
                <div className={styles.text}>Вход с нового устройства. Мы отправили код {sentTo(status)}.</div>
                {status.device && <div className={styles.device}>{status.device}</div>}

                <CodeCells
                    value={code}
                    invalid={!!error}
                    disabled={busy}
                    inputRef={input}
                    onChange={(value) => {
                        setCode(value);
                        setError(null);
                        value.length === CODE_LENGTH && verify(value);
                    }}
                />

                {error ? (
                    <div className={styles.error}>{error}</div>
                ) : (
                    <div className={styles.hint}>
                        {status.codeActive ? "Код действует 10 минут" : "Код устарел или не пришёл — отправьте новый"}
                    </div>
                )}
            </div>

            <div className={`${Classes.DIALOG_FOOTER} ${styles.footer}`}>
                <PlanrButton
                    type="blueish"
                    size="small"
                    icon="general-sign-in"
                    disabled={busy || code.length !== CODE_LENGTH}
                    onClick={() => verify(code)}
                    style={{ width: "100%" }}
                >
                    Подтвердить
                </PlanrButton>

                <div className={styles.links}>
                    <button type="button" className={styles.link} disabled={busy || waitLeft > 0} onClick={resend}>
                        {waitLeft > 0 ? `Отправить код снова через ${formatWait(waitLeft)}` : "Отправить код ещё раз"}
                    </button>
                    {back}
                </div>
            </div>
        </>
    );
});

/** «в Telegram и на i***v@yandex.ru» */
const sentTo = (status: DeviceStatus) => {
    const where: string[] = [];
    status.telegram && where.push("в Telegram");
    status.email && where.push(`на ${status.email}`);
    return where.join(" и ");
};

const formatWait = (seconds: number) => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

interface CodeCellsProps {
    value: string;
    invalid: boolean;
    disabled: boolean;
    inputRef: React.RefObject<HTMLInputElement | null>;
    onChange: (value: string) => void;
}

/**
 * Шесть ячеек под цифры. Под ними одно настоящее поле: вставка кода целиком,
 * подстановка кода из SMS/почты браузером и телефоном (one-time-code) работают как обычно
 */
const CodeCells = ({ value, invalid, disabled, inputRef, onChange }: CodeCellsProps) => {
    const [focused, setFocused] = useState(true);

    return (
        <label className={`${styles.cells} ${invalid ? styles.cellsInvalid : ""}`}>
            <input
                ref={inputRef}
                className={styles.cellsInput}
                value={value}
                disabled={disabled}
                autoFocus={true}
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label="Код подтверждения"
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onChange={(e) => onChange(e.currentTarget.value.replace(/\D/g, "").slice(0, CODE_LENGTH))}
            />
            {Array.from({ length: CODE_LENGTH }, (_, i) => (
                <span
                    key={i}
                    className={`${styles.cell} ${value[i] ? styles.cellFilled : ""} ${
                        focused && i === Math.min(value.length, CODE_LENGTH - 1) ? styles.cellCurrent : ""
                    }`}
                >
                    {value[i] ?? ""}
                </span>
            ))}
        </label>
    );
};
